/**
 * Home recap section v2 (recap/pill redesign §2) — question-card-size recap
 * cards INSIDE the feed.
 *
 * Renders every in-window recap from `useLiveRecaps()` (`GET /events/my-recaps`
 * — going-scoped, 48h server window, `preview_users` enriched) as a `SwipeRow`
 * of cards: event image + name + when it ended, goer facepile + people/posts
 * counts, and the "Remember who you met" CTA opening the roster drawer. Home
 * threads it into `HomeFeed` via `injectedItem`, where it interleaves as its
 * own keyed list item at ~index 3 (§2 placement — the injection is pure
 * render-time; feed keys and pagination never change). It owns its own
 * `useNow` tick through `useLiveRecaps`, so React re-renders only this subtree
 * on the 30s tick — the sibling feed cards and their `HomeFeed` parent never
 * re-render (spec §3).
 *
 * NO ✕ anywhere (§2): a card hides ONLY after the viewer MARKS at least one
 * person in the roster for that event. The whole hide-on-mark orchestration
 * (row pre-filter, open-drawer exemption,
 * ONE feed-counts batch, per-card writer + impression) is the SHARED
 * `useRecapRow`/`useRecapCard` pair — see that module for the contract; this
 * file only supplies the Home scopes and surface. Already-marked events
 * (this or any device, once connections data is at hand) derive hidden
 * passively. The 48h expiry is the ceiling
 * regardless — server-side via my-recaps, with the aligned client phase gate
 * in `useLiveRecaps`.
 */
import { useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoingFacepile } from '@components/GoingFacepile';
import { selectGoingFaces } from '@components/goingFaces';
import { FOCUS_RING_CLASSES, WeMetRecapCta } from '@components/live';
import { SwipeRow } from '@components/ui/SwipeRow';
import { ImgWithFallback } from '@components/ui/img-with-fallback';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useLiveRecaps } from '@hooks/useLiveRecaps';
import { useRecapCard, useRecapRow } from '@hooks/useRecapRow';
import { ANALYTICS_EVENTS, type LiveSurface } from '@lib/analytics-events';
import { buildEventContextPayload, buildEventParentSpacePayload } from '@lib/analytics/payloads';
import { cn } from '@lib/utils';
import type { EventRead } from '@models/api';
import { routes } from '@routes';
import { getEventGoingCount } from '@utils/eventGoingCount';
import { formatEndedAgo } from '@utils/eventLiveness';
import { RECAP_ATTENDED_LABEL, composeRecapPeople, composeRecapPosts } from './liveHomeCopy';

/** Analytics surface for every event this section fires (spec §9). */
const SURFACE: LiveSurface = 'home_recap';
/** The Home recap's own persisted mark-hide scope (§2 — never shared with the strip). */
const MARK_SCOPE = 'home-recap';

/**
 * The card facepile's face-selection rule — the pill v2 constant (§1/§2):
 * pictures or initials both qualify. ONE constant feeds the render gate and
 * the pile, so what decides the row is exactly what would render.
 */
const RECAP_FACES_FILTER = 'picture-or-name' as const;

export function LiveHomeRecap() {
  const { events, now } = useLiveRecaps();

  // The shared recap-row orchestration (see useRecapRow): hidden pre-filter
  // (persisted + derived sources) with the open-drawer entry
  // exempt, plus the section's ONE feed-counts batch (T-8).
  const { visible, openEventId, setOpenEventId, getFeedCount, isCountPending } = useRecapRow(
    events,
    { markScope: MARK_SCOPE },
  );
  // Names the rail's group for AT (aria-labelledby → the visible section label).
  const sectionLabelId = useId();

  if (visible.length === 0) {
    return null;
  }

  return (
    <div>
      {/* Section label (recap-label redesign): ONE muted title above the rail,
          replacing the former per-card eyebrow. The rail is going-scoped, so
          "You were there" is truthful for every card beneath it. */}
      <p
        id={sectionLabelId}
        className="mb-1.5 px-1 text-xs font-semibold uppercase text-mb-gray-text"
      >
        {RECAP_ATTENDED_LABEL}
      </p>
      <SwipeRow ariaLabelledBy={sectionLabelId} depKey={visible.map(event => event.id).join('|')}>
        {visible.map(event => (
          <LiveHomeRecapCard
            // Row slots and cards keyed by event.id (§2 review constraint).
            key={event.id}
            event={event}
            now={now}
            postsCount={getFeedCount(event.id) ?? null}
            postsPending={isCountPending(event.id)}
            rosterOpen={openEventId === event.id}
            onRosterOpenChange={next => {
              setOpenEventId(next ? event.id : null);
            }}
          />
        ))}
      </SwipeRow>
    </div>
  );
}

interface LiveHomeRecapCardProps {
  event: EventRead;
  /** The surface tick (one clock per surface tree, W-9) — drives the ended-ago line. */
  now: Date;
  /** The viewer's feed count for this event (null = nothing to show). */
  postsCount: number | null;
  /** True only during the authed first fetch of the host's batch, per id. */
  postsPending: boolean;
  /** Controlled roster-drawer visibility — the row owns it (row-level deferWhile). */
  rosterOpen: boolean;
  onRosterOpenChange: (open: boolean) => void;
}

function LiveHomeRecapCard({
  event,
  now,
  postsCount,
  postsPending,
  rosterOpen,
  onRosterOpenChange,
}: LiveHomeRecapCardProps) {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitudeTracking();
  // Shared card wrapper (§2 — see useRecapCard): hide-on-mark writer + gate
  // (deferWhile = the open drawer) and the once-per-mount impression a
  // hidden card never fires.
  const { hidden, onMarked } = useRecapCard(event, {
    markScope: MARK_SCOPE,
    surface: SURFACE,
    rosterOpen,
  });

  // Belt: the row's pre-filter is the slot-level gate; this covers the same
  // inputs per card so the two can never disagree for more than a render.
  if (hidden) {
    return null;
  }

  const goToEvent = () => {
    trackEvent(ANALYTICS_EVENTS.live_surface_tapped, {
      surface: SURFACE,
      // Matches the card's impression (useRecapCard): constant on this surface —
      // the row is sourced from the ended-event recap feed — but present, so the
      // displayed→tapped pair carries the same dimension everywhere.
      phase: 'ended-recap',
      ...buildEventContextPayload(event),
      ...buildEventParentSpacePayload(event),
    });
    navigate(routes.event.details(event, { returnTo: routes.home() }));
  };

  const faces = selectGoingFaces(event.preview_users, RECAP_FACES_FILTER);
  const people = composeRecapPeople(event);
  const posts = composeRecapPosts(postsCount);
  const countsText = [people, posts].filter((segment): segment is string => segment !== null);
  const hasMetaRow = faces.length > 0 || countsText.length > 0 || postsPending;

  return (
    // Question-card size (§2): the QuestionCard shell tokens, so the card sits
    // in the feed as a peer item.
    <div className="w-full rounded-[20px] bg-neutral-50 p-4 shadow-xs">
      <button
        type="button"
        onClick={goToEvent}
        className={cn(
          'flex w-full flex-col gap-3 rounded-lg text-left',
          // Pressed cue: the wide body tints instead of scaling (the
          // LiveCompactCard body treatment).
          'transition-colors duration-[160ms] ease-out active:bg-black/5',
          FOCUS_RING_CLASSES,
        )}
      >
        <span className="flex w-full items-center gap-3">
          <ImgWithFallback
            src={encodeURI(event.image_url || '/meetball-logo.png')}
            alt=""
            loading="lazy"
            className="h-12 w-12 shrink-0 rounded-xl bg-gray-100 object-cover"
            // Matches CommunityGridCard's banner fallback: on error, fall back
            // to a plain tinted box rather than a second request for the same
            // `/meetball-logo.png` URL that just failed. The wrapping <span>
            // carries no background of its own, so the box is rebuilt fresh
            // here (same geometry as the <img>, minus object-cover).
            fallback={() => (
              <div aria-hidden="true" className="h-12 w-12 shrink-0 rounded-xl bg-gray-100" />
            )}
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm font-medium text-gray-900">{event.name}</span>
            <span className="text-xs text-mb-gray-text">{formatEndedAgo(event, now)}</span>
          </span>
        </span>
        {hasMetaRow ? (
          <span className="flex w-full items-center gap-2">
            {faces.length > 0 ? (
              // Decorative compression (§0): the visible people count beside it
              // carries the number, so the pile is aria-hidden with no srLabel.
              <span aria-hidden="true" className="shrink-0">
                <GoingFacepile
                  users={event.preview_users}
                  faceSizeClasses="h-6 w-6"
                  ringClass="ring-white"
                  filter={RECAP_FACES_FILTER}
                  totalCount={getEventGoingCount(event)}
                />
              </span>
            ) : null}
            <span className="flex min-w-0 items-center text-xs text-mb-gray-text tabular-nums">
              <span className="truncate">{countsText.join(' · ')}</span>
              {posts === null && postsPending ? (
                // Posts count still loading (T-8): a small shimmer sized like
                // `· 8 posts`. ≥1 gate untouched — a pending count may resolve
                // to 0 and render nothing. aria-hidden: the real text nodes are
                // the a11y content, a blank bar is not.
                <span aria-hidden="true" className="flex shrink-0 items-center whitespace-pre">
                  {countsText.length > 0 ? ' · ' : ''}
                  <span className="h-3 w-11 animate-pulse rounded bg-gray-200" />
                </span>
              ) : null}
            </span>
          </span>
        ) : null}
      </button>
      <WeMetRecapCta
        eventId={event.id}
        eventName={event.name}
        open={rosterOpen}
        onOpenChange={next => {
          if (next) {
            trackEvent(ANALYTICS_EVENTS.live_recap_we_met_clicked, {
              surface: SURFACE,
              ...buildEventContextPayload(event),
              ...buildEventParentSpacePayload(event),
            });
          }
          onRosterOpenChange(next);
        }}
        onMarked={onMarked}
        // w-fit: as a block-level flex child the tappable would stretch the
        // full card width — keep the hit box on the chip (still ≥44px tall).
        className="mt-1.5 -mb-1.5 w-fit"
      />
    </div>
  );
}
